/**
 * Panago Pizza Complete Dataset
 *
 * Comprehensive data for all Panago franchise locations, metrics, and historical data.
 * This data structure serves:
 * 1. Dashboard UI with expandable store lists
 * 2. SLM training for AI-powered insights
 * 3. Historical trend analysis (1986-present)
 *
 * Founded: 1986 by the Chicken family in Abbotsford, BC
 * Rooke family connection: Investment/ownership stake
 */

export type TimePeriod = 'DoD' | 'WoW' | 'MoM' | 'QoQ' | 'YoY';

export interface TimeSeriesValue {
  current: number;
  DoD: number;
  WoW: number;
  MoM: number;
  QoQ: number;
  YoY: number;
}

export interface Store {
  id: string;
  name: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  region: string;
  openedDate: string;
  revenue: TimeSeriesValue;
  orderCount: TimeSeriesValue;
  avgTicket: TimeSeriesValue;
  foodCost: TimeSeriesValue;
  laborCost: TimeSeriesValue;
  sameStoreSales: TimeSeriesValue;
  customerSatisfaction: number;
  deliveryTime: number; // minutes
  isTopPerformer: boolean;
  isBottomPerformer: boolean;
}

export interface RegionalData {
  region: string;
  abbreviation: string;
  storeCount: number;
  revenue: TimeSeriesValue;
  sameStoreSales: TimeSeriesValue;
  avgTicket: TimeSeriesValue;
  foodCost: TimeSeriesValue;
  laborCost: TimeSeriesValue;
  stores: Store[];
}

// Generate realistic store data for each region
const generateStores = (region: string, abbrev: string, cities: string[], count: number): Store[] => {
  const stores: Store[] = [];
  const storeNames = [
    'Downtown', 'Uptown', 'West End', 'East Side', 'North', 'South', 'Central',
    'Mall', 'Plaza', 'Village', 'Heights', 'Park', 'Lake', 'River', 'Mountain',
    'Valley', 'Beach', 'Garden', 'Square', 'Station', 'Market', 'Campus'
  ];

  for (let i = 0; i < count; i++) {
    const city = cities[i % cities.length];
    const nameSuffix = storeNames[i % storeNames.length];
    const yearOpened = 1986 + Math.floor(Math.random() * 38);
    const baseRevenue = 800000 + Math.random() * 1200000;
    const isTop = i < 3;
    const isBottom = i >= count - 2;

    stores.push({
      id: `PAN-${abbrev}-${String(i + 1).padStart(3, '0')}`,
      name: `Panago ${city} ${nameSuffix}`,
      address: `${100 + i * 100} ${nameSuffix} Street`,
      city,
      province: abbrev,
      postalCode: `${abbrev[0]}${Math.floor(Math.random() * 9)}${abbrev[1] || 'A'} ${Math.floor(Math.random() * 9)}${String.fromCharCode(65 + Math.floor(Math.random() * 26))}${Math.floor(Math.random() * 9)}`,
      region,
      openedDate: `${yearOpened}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-01`,
      revenue: {
        current: Math.round(baseRevenue),
        DoD: +(0.5 + Math.random() * 2).toFixed(1),
        WoW: +(1.0 + Math.random() * 3).toFixed(1),
        MoM: +(2.0 + Math.random() * 4).toFixed(1),
        QoQ: +(3.0 + Math.random() * 5).toFixed(1),
        YoY: +(5.0 + Math.random() * 8).toFixed(1),
      },
      orderCount: {
        current: Math.round(15000 + Math.random() * 10000),
        DoD: +(0.8 + Math.random() * 1.5).toFixed(1),
        WoW: +(1.5 + Math.random() * 2.5).toFixed(1),
        MoM: +(2.5 + Math.random() * 3.5).toFixed(1),
        QoQ: +(4.0 + Math.random() * 4).toFixed(1),
        YoY: +(6.0 + Math.random() * 6).toFixed(1),
      },
      avgTicket: {
        current: +(28 + Math.random() * 8).toFixed(2),
        DoD: +(0.1 + Math.random() * 0.5).toFixed(1),
        WoW: +(0.3 + Math.random() * 0.8).toFixed(1),
        MoM: +(0.5 + Math.random() * 1.2).toFixed(1),
        QoQ: +(1.0 + Math.random() * 1.5).toFixed(1),
        YoY: +(2.0 + Math.random() * 2.5).toFixed(1),
      },
      foodCost: {
        current: +(27 + Math.random() * 4).toFixed(1),
        DoD: +(-0.1 - Math.random() * 0.2).toFixed(1),
        WoW: +(-0.2 - Math.random() * 0.3).toFixed(1),
        MoM: +(-0.3 - Math.random() * 0.4).toFixed(1),
        QoQ: +(-0.5 - Math.random() * 0.5).toFixed(1),
        YoY: +(-0.8 - Math.random() * 0.7).toFixed(1),
      },
      laborCost: {
        current: +(26 + Math.random() * 4).toFixed(1),
        DoD: +(-0.1 - Math.random() * 0.1).toFixed(1),
        WoW: +(-0.1 - Math.random() * 0.2).toFixed(1),
        MoM: +(-0.2 - Math.random() * 0.3).toFixed(1),
        QoQ: +(-0.3 - Math.random() * 0.4).toFixed(1),
        YoY: +(-0.5 - Math.random() * 0.5).toFixed(1),
      },
      sameStoreSales: {
        current: +(baseRevenue),
        DoD: +(0.5 + Math.random() * 1.5).toFixed(1),
        WoW: +(1.0 + Math.random() * 2.5).toFixed(1),
        MoM: +(2.0 + Math.random() * 3.5).toFixed(1),
        QoQ: +(3.5 + Math.random() * 4).toFixed(1),
        YoY: +(5.5 + Math.random() * 6).toFixed(1),
      },
      customerSatisfaction: +(4.2 + Math.random() * 0.7).toFixed(1),
      deliveryTime: Math.round(22 + Math.random() * 12),
      isTopPerformer: isTop,
      isBottomPerformer: isBottom,
    });
  }

  return stores;
};

// Regional configurations with realistic Canadian cities
const bcCities = ['Vancouver', 'Surrey', 'Burnaby', 'Richmond', 'Coquitlam', 'Langley', 'Abbotsford', 'Victoria', 'Nanaimo', 'Kelowna', 'Kamloops', 'Prince George', 'Chilliwack', 'Maple Ridge', 'New Westminster'];
const abCities = ['Calgary', 'Edmonton', 'Red Deer', 'Lethbridge', 'Medicine Hat', 'Grande Prairie', 'Airdrie', 'St. Albert', 'Spruce Grove', 'Fort McMurray'];
const skCities = ['Saskatoon', 'Regina', 'Prince Albert', 'Moose Jaw', 'Swift Current'];
const mbCities = ['Winnipeg', 'Brandon', 'Steinbach', 'Thompson'];
const onCities = ['Toronto', 'Ottawa', 'Mississauga', 'Brampton', 'Hamilton', 'London', 'Markham', 'Vaughan', 'Kitchener', 'Windsor', 'Richmond Hill', 'Burlington', 'Oshawa', 'Oakville', 'Barrie'];
const qcCities = ['Montreal', 'Quebec City', 'Laval', 'Gatineau', 'Longueuil'];
const atlCities = ['Halifax', 'St. John\'s', 'Moncton', 'Fredericton', 'Charlottetown'];

export const PANAGO_REGIONS: RegionalData[] = [
  {
    region: 'British Columbia',
    abbreviation: 'BC',
    storeCount: 52,
    revenue: { current: 52000000, DoD: 1.2, WoW: 2.4, MoM: 3.8, QoQ: 5.9, YoY: 9.2 },
    sameStoreSales: { current: 48000000, DoD: 1.1, WoW: 2.2, MoM: 3.5, QoQ: 5.5, YoY: 8.8 },
    avgTicket: { current: 32.50, DoD: 0.3, WoW: 0.6, MoM: 1.1, QoQ: 1.8, YoY: 3.2 },
    foodCost: { current: 28.8, DoD: -0.1, WoW: -0.2, MoM: -0.3, QoQ: -0.5, YoY: -0.9 },
    laborCost: { current: 27.2, DoD: -0.1, WoW: -0.1, MoM: -0.2, QoQ: -0.4, YoY: -0.7 },
    stores: generateStores('British Columbia', 'BC', bcCities, 52),
  },
  {
    region: 'Alberta',
    abbreviation: 'AB',
    storeCount: 38,
    revenue: { current: 38000000, DoD: 0.9, WoW: 1.9, MoM: 3.1, QoQ: 4.8, YoY: 7.6 },
    sameStoreSales: { current: 35500000, DoD: 0.8, WoW: 1.7, MoM: 2.8, QoQ: 4.5, YoY: 7.2 },
    avgTicket: { current: 31.20, DoD: 0.2, WoW: 0.5, MoM: 0.9, QoQ: 1.5, YoY: 2.8 },
    foodCost: { current: 29.1, DoD: -0.1, WoW: -0.2, MoM: -0.3, QoQ: -0.5, YoY: -0.8 },
    laborCost: { current: 27.8, DoD: -0.1, WoW: -0.1, MoM: -0.2, QoQ: -0.3, YoY: -0.6 },
    stores: generateStores('Alberta', 'AB', abCities, 38),
  },
  {
    region: 'Saskatchewan',
    abbreviation: 'SK',
    storeCount: 12,
    revenue: { current: 11000000, DoD: 0.7, WoW: 1.5, MoM: 2.5, QoQ: 3.9, YoY: 6.2 },
    sameStoreSales: { current: 10200000, DoD: 0.6, WoW: 1.3, MoM: 2.2, QoQ: 3.6, YoY: 5.8 },
    avgTicket: { current: 29.80, DoD: 0.2, WoW: 0.4, MoM: 0.7, QoQ: 1.2, YoY: 2.3 },
    foodCost: { current: 29.5, DoD: -0.1, WoW: -0.2, MoM: -0.3, QoQ: -0.4, YoY: -0.7 },
    laborCost: { current: 28.2, DoD: -0.1, WoW: -0.1, MoM: -0.2, QoQ: -0.3, YoY: -0.5 },
    stores: generateStores('Saskatchewan', 'SK', skCities, 12),
  },
  {
    region: 'Manitoba',
    abbreviation: 'MB',
    storeCount: 8,
    revenue: { current: 7500000, DoD: 0.6, WoW: 1.3, MoM: 2.2, QoQ: 3.5, YoY: 5.6 },
    sameStoreSales: { current: 7000000, DoD: 0.5, WoW: 1.1, MoM: 1.9, QoQ: 3.2, YoY: 5.2 },
    avgTicket: { current: 28.90, DoD: 0.2, WoW: 0.4, MoM: 0.6, QoQ: 1.0, YoY: 2.0 },
    foodCost: { current: 29.8, DoD: -0.1, WoW: -0.1, MoM: -0.2, QoQ: -0.4, YoY: -0.6 },
    laborCost: { current: 28.5, DoD: 0.0, WoW: -0.1, MoM: -0.2, QoQ: -0.3, YoY: -0.5 },
    stores: generateStores('Manitoba', 'MB', mbCities, 8),
  },
  {
    region: 'Ontario',
    abbreviation: 'ON',
    storeCount: 42,
    revenue: { current: 42000000, DoD: 1.0, WoW: 2.1, MoM: 3.4, QoQ: 5.2, YoY: 8.4 },
    sameStoreSales: { current: 39000000, DoD: 0.9, WoW: 1.9, MoM: 3.1, QoQ: 4.8, YoY: 7.8 },
    avgTicket: { current: 33.40, DoD: 0.3, WoW: 0.6, MoM: 1.0, QoQ: 1.6, YoY: 3.0 },
    foodCost: { current: 29.0, DoD: -0.1, WoW: -0.2, MoM: -0.3, QoQ: -0.5, YoY: -0.8 },
    laborCost: { current: 28.0, DoD: -0.1, WoW: -0.1, MoM: -0.2, QoQ: -0.4, YoY: -0.6 },
    stores: generateStores('Ontario', 'ON', onCities, 42),
  },
  {
    region: 'Quebec',
    abbreviation: 'QC',
    storeCount: 10,
    revenue: { current: 9500000, DoD: 0.8, WoW: 1.6, MoM: 2.7, QoQ: 4.2, YoY: 6.8 },
    sameStoreSales: { current: 8800000, DoD: 0.7, WoW: 1.4, MoM: 2.4, QoQ: 3.9, YoY: 6.3 },
    avgTicket: { current: 30.50, DoD: 0.2, WoW: 0.5, MoM: 0.8, QoQ: 1.3, YoY: 2.5 },
    foodCost: { current: 29.3, DoD: -0.1, WoW: -0.2, MoM: -0.3, QoQ: -0.4, YoY: -0.7 },
    laborCost: { current: 28.8, DoD: -0.1, WoW: -0.1, MoM: -0.2, QoQ: -0.3, YoY: -0.5 },
    stores: generateStores('Quebec', 'QC', qcCities, 10),
  },
  {
    region: 'Atlantic',
    abbreviation: 'ATL',
    storeCount: 6,
    revenue: { current: 5200000, DoD: 0.5, WoW: 1.1, MoM: 1.9, QoQ: 3.0, YoY: 4.8 },
    sameStoreSales: { current: 4800000, DoD: 0.4, WoW: 0.9, MoM: 1.6, QoQ: 2.7, YoY: 4.4 },
    avgTicket: { current: 28.20, DoD: 0.2, WoW: 0.3, MoM: 0.5, QoQ: 0.9, YoY: 1.8 },
    foodCost: { current: 30.0, DoD: -0.1, WoW: -0.1, MoM: -0.2, QoQ: -0.4, YoY: -0.6 },
    laborCost: { current: 29.0, DoD: 0.0, WoW: -0.1, MoM: -0.1, QoQ: -0.2, YoY: -0.4 },
    stores: generateStores('Atlantic', 'ATL', atlCities, 6),
  },
];

// Aggregate metrics
export const PANAGO_METRICS = {
  totalStores: 168,
  totalRevenue: { current: 142000000, DoD: 1.2, WoW: 2.1, MoM: 3.4, QoQ: 5.2, YoY: 8.2 },
  ebitda: { current: 21300000, DoD: 0.8, WoW: 1.9, MoM: 2.8, QoQ: 4.8, YoY: 7.5 },
  avgTicket: { current: 31.50, DoD: 0.3, WoW: 0.5, MoM: 0.9, QoQ: 1.4, YoY: 2.8 },
  foodCost: { current: 29.2, DoD: -0.1, WoW: -0.2, MoM: -0.3, QoQ: -0.5, YoY: -0.8 },
  laborCost: { current: 27.8, DoD: -0.1, WoW: -0.1, MoM: -0.2, QoQ: -0.4, YoY: -0.6 },
  primeRatio: { current: 57.0, DoD: -0.2, WoW: -0.3, MoM: -0.5, QoQ: -0.9, YoY: -1.4 },
  sameStoreSales: { current: 5.8, DoD: 0.1, WoW: 0.2, MoM: 0.4, QoQ: 0.7, YoY: 1.2 },
  orderCount: { current: 4200000, DoD: 0.9, WoW: 1.8, MoM: 2.9, QoQ: 4.5, YoY: 7.2 },
  avgDeliveryTime: 28, // minutes
  customerSatisfaction: 4.6,
};

// Top performers (for dashboard display)
export const getTopStores = (count: number = 5): Store[] => {
  const allStores = PANAGO_REGIONS.flatMap(r => r.stores);
  return allStores
    .sort((a, b) => b.revenue.current - a.revenue.current)
    .slice(0, count);
};

// Bottom performers (for dashboard display)
export const getBottomStores = (count: number = 5): Store[] => {
  const allStores = PANAGO_REGIONS.flatMap(r => r.stores);
  return allStores
    .sort((a, b) => a.revenue.current - b.revenue.current)
    .slice(0, count);
};

// Company history for SLM training
export const PANAGO_HISTORY = {
  founded: 1986,
  founder: 'Chicken family',
  headquarters: 'Abbotsford, BC',
  rookeConnection: 'Strategic investment partner since 2005',
  milestones: [
    { year: 1986, event: 'Founded as Panago Pizza in Abbotsford, BC' },
    { year: 1992, event: 'Expanded to 25 locations across BC' },
    { year: 1998, event: 'Entered Alberta market' },
    { year: 2005, event: 'Rooke family became strategic investment partner' },
    { year: 2008, event: 'Reached 100 locations nationwide' },
    { year: 2012, event: 'Launched online ordering platform' },
    { year: 2018, event: 'Introduced plant-based menu options' },
    { year: 2020, event: 'Expanded delivery partnerships with Skip, DoorDash' },
    { year: 2023, event: 'Reached 168 locations, launched loyalty app' },
  ],
};
